import { inject, Injectable } from '@angular/core';
import { DbService } from '../db/db.service';
import { Project } from '../../models/project';
import { Category } from '../../models/category';
import { LanguageCategory } from '../../models/languageCategory';
import { isNullOrUndefined, isValidFunction } from '@pineapplelab/util';
import firebase from "firebase/compat/app";
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class LanguageCategoryService {

	private db = inject(DbService);

	private getSubCollectionPath(categoryId: string): firebase.firestore.CollectionReference {
		return this.db.getProyIdRef(environment.projectId)
			.collection(Category.PATH)
			.doc(categoryId)
			.collection(LanguageCategory.PATH);
	}

	async getDefault(categoryId: string, projectId: string) {
		const snapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(Category.PATH)
			.doc(categoryId)
			.collection(LanguageCategory.PATH)
			.where('is_default', '==', true)
			.get();

		if (snapshot.empty) {
			return null;
		}

		const languageCategoryDoc = snapshot.docs[0];
		const languageCategory = new LanguageCategory(languageCategoryDoc.data());
		return languageCategory;
	}

	async save(model: LanguageCategory, projectId: string, categoryId: string) {
		if (isNullOrUndefined(model.id)) {
			model.id = this.db.generateFirebaseUniqueId();
		}
		const json = model.toJson();
		await this.db.getProyIdRef(projectId).collection(Category.PATH)
			.doc(categoryId).collection(LanguageCategory.PATH).doc(model.id).set(json, { merge: true });
	}

	async getAllById(categoryId: string, projectId: string) {
		const snapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(Category.PATH)
			.doc(categoryId)
			.collection(LanguageCategory.PATH)
			.where('is_deleted', '==', false)
			.get();

		if (snapshot.empty) {
			return null;
		}

		const languageCategories = snapshot.docs.map(languageCategory => new LanguageCategory(languageCategory.data()));
		return languageCategories;
	}

	async deleteByCategoryId(projectId: string, categoryId: string) {
		const languageCategoriesSnapshot = await this.db.firestore
		.collection(Project.PATH)
		.doc(projectId)
		.collection(Category.PATH)
		.doc(categoryId)
		.collection(LanguageCategory.PATH)
		.get();

		const batch = this.db.firestore.batch();

		languageCategoriesSnapshot.docs.forEach(doc => {
		  batch.delete(doc.ref);
		});

		await batch.commit();
	}

	observe(
		categoryId: string,
		next: (snapshot: firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>) => void,
		error: (error: firebase.firestore.FirestoreError) => void
	) {
		if (!categoryId || !isValidFunction(next) || !isValidFunction(error)) {
			return null;
		}

		return this.getSubCollectionPath(categoryId).onSnapshot(next as any, error);
	}

}
